/**
 * Impact & Growth Platform adoption — slide 44 of the finalised pack.
 *
 * Five measures, every one a share of the SAME eligible employees: permanent and
 * fixed-term employees in post who were first appointed on or before the cycle's
 * cut-off (31 March). Independent contractors and casual workers are not part of the
 * review cycle, so they are not on this panel at all — employee-first by definition.
 *
 * A copy stored before this definition (the check-in roster, waves, objectives,
 * campaigns, the "not in use" register) is served exactly as stored. None of it is
 * shown: its roster counted contractors and casual workers, and slide 44 retired
 * those figures. Such a copy reads "not yet available for this date".
 *
 * Withheld and not available are different answers. A served null is WITHHELD to
 * protect a small group — a dash with the rule attached, never a zero and never a
 * bar. A key the response does not carry is NOT AVAILABLE FOR THIS DATE. Permanent
 * and fixed-term add up to the measure, so identity withholds them together; this
 * panel never works either out from the total, and never computes a percentage it
 * was not sent.
 */
import React, { useMemo, type ReactNode } from 'react';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  INK,
  Def,
  fmtDay,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type AdoptionCell,
  type AdoptionExtra,
  type AdoptionMeasureKey,
  type AdoptionStored,
  type DefLookup,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  /** Part of the shared panel signature. Eligible employees are the same on every
   *  basis — the toggle cannot change who took part in the review cycle — which is
   *  exactly what the window line tells the reader. */
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

/** The five measures of slide 44, in the pack's order. Each always has a row: one the
 *  response does not carry reads "not available for this date" rather than vanishing. */
const MEASURE_ORDER: AdoptionMeasureKey[] = [
  'onboarded',
  'checkin_completed',
  'calibrated',
  'profile_complete',
  'growth_plan',
];

const MEASURE_LABEL: Record<AdoptionMeasureKey, string> = {
  onboarded: 'Onboarded onto the platform',
  checkin_completed: 'Completed the check-in',
  calibrated: 'Carry a calibrated rating',
  profile_complete: 'Completed a personal profile',
  growth_plan: 'Hold an individual growth plan',
};

const CATEGORIES: { key: 'Permanent' | 'FTC'; label: string }[] = [
  { key: 'Permanent', label: 'Permanent' },
  { key: 'FTC', label: 'Fixed-term' },
];

const PROFILE_ROWS: { key: 'qualifications' | 'career_history' | 'either' | 'both'; label: string }[] = [
  { key: 'qualifications', label: 'Qualifications captured' },
  { key: 'career_history', label: 'Career history captured' },
  { key: 'either', label: 'At least one of the two' },
  { key: 'both', label: 'Both — a complete personal profile' },
];

const WITHHELD_TIP =
  'Withheld to protect small groups: fewer than five people. It is not zero. Permanent and '
  + 'fixed-term add up to the measure, so both are withheld together and neither can be worked out '
  + 'from the total.';

/** The current definition carries the population and the measures; a copy stored before it carries neither. */
const isCurrent = (ad: AdoptionExtra | AdoptionStored | null): ad is AdoptionExtra =>
  !!ad
  && typeof ad.measures === 'object'
  && ad.measures !== null
  && typeof ad.population === 'object'
  && ad.population !== null;

// =============================================================================
// DEFINITIONS — /metric-definitions is the source of truth; these only fill the
// keys it does not publish, so no label here is ever left without one.
// =============================================================================

const defBody = (label: string, body: string, caveats?: string[]): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveats?.length ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        {caveats.map((c, i) => (
          <li key={i}>{c}</li>
        ))}
      </ul>
    ) : null}
  </div>
);

const FALLBACK_DEFS: Record<string, ReactNode> = {
  adoption: defBody(
    'Impact & Growth Platform adoption',
    'How far eligible employees have taken up the platform in the current review cycle: onboarding, '
      + 'the check-in conversation, a calibrated rating, a personal profile and an individual growth '
      + 'plan. Every measure is a share of the same eligible employees.',
    [
      'The review cycle as it stands on the date shown; figures rise as reviews are completed and calibrated.',
      'Independent contractors and casual workers are not part of the review cycle and are not counted.',
    ],
  ),
  adoption_eligible: defBody(
    'Eligible employees',
    'Permanent and fixed-term employees in post who were first appointed on or before 31 March of the '
      + 'cycle’s year. Someone who joined inside the cycle could not take part in it.',
    ['A person re-employed after leaving counts from their first appointment.'],
  ),
  adoption_onboarded: defBody(
    'Onboarded onto the platform',
    'Eligible employees who have completed the platform’s onboarding steps.',
  ),
  adoption_checkin_completed: defBody(
    'Completed the check-in',
    'Eligible employees whose check-in conversation for this cycle has been held and submitted.',
  ),
  adoption_calibrated: defBody(
    'Carry a calibrated rating',
    'Eligible employees whose rating has been confirmed in a calibration panel. Appointed too recently '
      + 'to rate does not count as a rating.',
  ),
  adoption_profile_complete: defBody(
    'Completed a personal profile',
    'Eligible employees who have captured both their qualifications and their career history.',
    ['The professional-context section is not counted: the platform creates it for everyone automatically.'],
  ),
  adoption_growth_plan: defBody(
    'Hold an individual growth plan',
    'Eligible employees with an individual growth plan for the cycle’s year.',
    ['A few plans are recorded against an identifier that matches no employee, so this is a floor.'],
  ),
  adoption_platform_account: defBody(
    'Hold a platform account',
    'Eligible employees who have an account on the Impact & Growth Platform. Onboarding, a personal '
      + 'profile and a growth plan all need one; the check-in and a calibrated rating do not.',
  ),
  adoption_profile_sections: defBody(
    'Personal profile sections',
    'Eligible employees with a platform account who have captured each section of their personal '
      + 'profile. A complete profile needs both.',
  ),
};

/** Server definition first, local wording only where the server has none. */
const extendDefs =
  (def: DefLookup, extra: Record<string, ReactNode>): DefLookup =>
  (key: string) =>
    def(key) ?? extra[key];

// =============================================================================
// WITHHELD AND NOT AVAILABLE — two different answers, never one dash
// =============================================================================

const Withheld: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span className="inline-flex items-center justify-end text-gray-400">
    <span aria-hidden="true">—</span>
    <span className="sr-only">withheld to protect small groups</span>
    <Def def={def} k="suppression" text={WITHHELD_TIP} />
  </span>
);

const NotAvailable: React.FC = () => (
  <span className="text-[11px] font-normal text-gray-400" title="Not available for this date">
    <span aria-hidden="true">not available</span>
    <span className="sr-only">not available for this date</span>
  </span>
);

/** A count: null is withheld; a key the response does not carry is not available. */
const Count: React.FC<{ v: number | null | undefined; def: DefLookup }> = ({ v, def }) =>
  v === null ? <Withheld def={def} /> : v === undefined ? <NotAvailable /> : <>{fmtNum(v)}</>;

/** A share of eligible employees. null: withheld with its count. Left out: no base, so nothing shown. */
const Pct: React.FC<{ v: number | null | undefined; def: DefLookup }> = ({ v, def }) =>
  v === null ? <Withheld def={def} /> : typeof v === 'number' ? <>{fmtPct(v)}</> : null;

/** A count flagged as withheld is never shown, whatever else the cell carries. */
const isWithheld = (cell: { people?: number | null; suppressed?: boolean }) =>
  cell.suppressed === true || cell.people === null;

const CategoryCell: React.FC<{ cell: AdoptionCell | undefined; def: DefLookup }> = ({ cell, def }) => {
  if (!cell) return <NotAvailable />;
  const withheld = isWithheld(cell);
  return (
    <div className="leading-tight">
      <div className="font-medium text-gray-700">
        {withheld ? <Withheld def={def} /> : <Count v={cell.people} def={def} />}
      </div>
      <div className="text-[10.5px] text-gray-500">
        {withheld ? <Withheld def={def} /> : <Pct v={cell.pct} def={def} />}
      </div>
    </div>
  );
};

const SectionLabel: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="mb-1.5 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </div>
);

// =============================================================================

export const AdoptionPanel: React.FC<Props> = ({ panels, loading, def }) => {
  const d = useMemo(() => extendDefs(def, FALLBACK_DEFS), [def]);
  const ad = readExtras(panels, 'adoption');
  const current = isCurrent(ad) ? ad : null;
  const pop = current?.population;

  // The served order first, then any of the five it left out, so a measure this
  // date does not carry still has its row and says so.
  const rows = useMemo(() => {
    if (!current) return [];
    const served = (current.measure_order ?? []).filter((k) =>
      Object.prototype.hasOwnProperty.call(MEASURE_LABEL, k),
    );
    return Array.from(new Set([...served, ...MEASURE_ORDER])).map((key) => ({
      key,
      m: current.measures[key],
    }));
  }, [current]);

  const cutoff = current?.eligibility?.appointed_on_or_before;
  const cutoffText = cutoff ? fmtDay(cutoff) : '31 March of the cycle’s year';
  const cycle = current?.cycle;
  const cycleText = cycle?.name
    ? `${cycle.name}${cycle.start_date && cycle.end_date ? `, ${fmtDay(cycle.start_date)} – ${fmtDay(cycle.end_date)}` : ''}`
    : null;

  const profile = current?.profile_sections;
  // `both` IS the complete-profile count. Where that measure is withheld, the same
  // figure is withheld here too, rather than shown beside the dash that hides it.
  const profileWithheld = !!current?.measures.profile_complete && isWithheld(current.measures.profile_complete);
  const unmatched = current?.growth_plan_records?.unmatched;

  return (
    <PanelShell
      title="Impact & Growth Platform adoption"
      defKey="adoption"
      def={d}
      window={
        `${panelDateLabel(panels, 'adoption')}${cycleText ? ` · ${cycleText}` : ''}. `
        + `Eligible employees are permanent and fixed-term staff appointed on or before ${cutoffText}; `
        + 'someone who joined inside the cycle could not take part in it. Independent contractors and '
        + 'casual workers are not part of the review cycle. Every measure is a share of the same eligible '
        + 'employees. A new review cycle takes over this panel once its first calibrated ratings are in. '
        + 'Follows neither the period picker nor the basis toggle.'
      }
      right={
        typeof pop?.eligible === 'number' ? (
          <div className="text-right">
            <div className="text-lg font-bold leading-none tabular-nums text-heritage-blue">
              {fmtNum(pop.eligible)}
            </div>
            <div className="text-[10px] text-gray-500">eligible employees</div>
          </div>
        ) : null
      }
      footnote={
        current ? (
          <>
            A personal profile means qualifications and career history both captured; the
            professional-context section is not counted because the platform creates it for everyone.
            {typeof unmatched === 'number' && unmatched > 0
              ? ` ${fmtNum(unmatched)} growth ${unmatched === 1 ? 'plan is' : 'plans are'} recorded against an identifier that matches no employee, so the growth-plan figure is a floor.`
              : ''}{' '}
            A dash marked withheld is a group of fewer than five, withheld to protect small groups — it
            is not zero, and permanent and fixed-term are withheld together. “Not available” means this
            date carries no such figure.
          </>
        ) : undefined
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={300} />
      ) : !panels ? (
        <PanelUnavailable what="Platform adoption" />
      ) : !current ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          {ad
            ? 'Adoption figures for eligible employees are not yet available for this date.'
            : 'Platform adoption is not available for this date.'}
        </div>
      ) : (
        <div className="space-y-5">
          {/* ── who is eligible ────────────────────────────────────────── */}
          <TileRow min="150px">
            <MetricTile
              label="Employees in post"
              value={<Count v={pop?.employees_in_post} def={d} />}
              sub="permanent and fixed-term"
              defKey="employees_in_post"
              def={d}
              size="sm"
            />
            <MetricTile
              label={`Appointed after ${cutoff ? fmtDay(cutoff) : 'the cut-off'}`}
              value={<Count v={pop?.appointed_after_cutoff} def={d} />}
              sub="joined inside the cycle, so not eligible"
              defKey="adoption_eligible"
              def={d}
              size="sm"
            />
            <MetricTile
              label="Eligible"
              value={<Count v={pop?.eligible} def={d} />}
              sub={
                <span className="inline-flex flex-wrap items-center gap-x-1">
                  <span>Permanent</span>
                  <Count v={pop?.eligible_by_category?.Permanent} def={d} />
                  <span>· Fixed-term</span>
                  <Count v={pop?.eligible_by_category?.FTC} def={d} />
                </span>
              }
              defKey="adoption_eligible"
              def={d}
              size="sm"
            />
            <MetricTile
              label="Hold a platform account"
              value={<Count v={pop?.with_platform_account} def={d} />}
              sub="eligible employees with an account"
              defKey="adoption_platform_account"
              def={d}
              size="sm"
            />
          </TileRow>

          {/* ── the five measures ──────────────────────────────────────── */}
          <div>
            <SectionLabel>
              Share of eligible employees
              <Def def={d} k="adoption" />
            </SectionLabel>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" aria-label="Adoption measures">
                <thead className="border-b text-gray-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Measure</th>
                    <th className="px-2 py-1.5 text-left font-medium" style={{ width: '26%' }}>
                      <span className="sr-only">Share of eligible employees</span>
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium">People</th>
                    <th className="px-2 py-1.5 text-right font-medium">Of eligible</th>
                    {CATEGORIES.map((c) => (
                      <th key={c.key} className="px-2 py-1.5 text-right font-medium">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ key, m }) => {
                    const withheld = !!m && isWithheld(m);
                    const pct = m && !withheld && typeof m.pct === 'number' ? m.pct : null;
                    return (
                      <tr key={key} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="px-2 py-1.5 text-gray-700">
                          <span className="inline-flex items-center">
                            {MEASURE_LABEL[key]}
                            <Def def={d} k={`adoption_${key}`} />
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="relative h-[13px] overflow-hidden rounded-sm bg-gray-100">
                            {pct !== null && pct > 0 ? (
                              <span
                                className="absolute inset-y-0 left-0 rounded-sm transition-[width] duration-300"
                                style={{ width: `${Math.min(100, Math.max(0.8, pct))}%`, background: INK }}
                              />
                            ) : null}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-heritage-blue">
                          {!m ? <NotAvailable /> : withheld ? <Withheld def={d} /> : <Count v={m.people} def={d} />}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                          {!m ? null : withheld ? <Withheld def={d} /> : <Pct v={m.pct} def={d} />}
                        </td>
                        {CATEGORIES.map((c) => (
                          <td key={c.key} className="px-2 py-1.5 text-right tabular-nums">
                            <CategoryCell cell={m?.by_category?.[c.key]} def={d} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-1 text-[11px] leading-snug text-gray-500">
              Every percentage is over the eligible employees in its own group: the measure over all{' '}
              <Count v={pop?.eligible} def={d} /> eligible, permanent over permanent and fixed-term over
              fixed-term.
            </div>
          </div>

          {/* ── profile sections ───────────────────────────────────────── */}
          {profile ? (
            <div>
              <SectionLabel>
                Personal profile sections
                <Def def={d} k="adoption_profile_sections" />
              </SectionLabel>
              <div className="overflow-x-auto">
                <table className="w-full max-w-[520px] text-xs" aria-label="Personal profile sections">
                  <tbody>
                    {PROFILE_ROWS.map((r) => (
                      <tr key={r.key} className="border-b last:border-0">
                        <td className="px-2 py-1 text-gray-700">{r.label}</td>
                        <td className="px-2 py-1 text-right font-medium tabular-nums text-gray-800">
                          {r.key === 'both' && profileWithheld ? (
                            <Withheld def={d} />
                          ) : (
                            <Count v={profile[r.key]} def={d} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-1 text-[11px] leading-snug text-gray-500">
                Eligible employees with a platform account. A complete profile needs both sections.
              </div>
            </div>
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
